import { FC, RefObject, useCallback, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import * as vp from 'vopay';
import { withStyles } from 'vopay';

import PlanSettingCard, { PlanSettingCardHandle } from '../../utils/PlanSettingCard/PlanSettingCard';
import { PLAN_MODEL_KEYS } from '../../utils/planSettingHelpers';

import styles from './AdditionalServices.module.scss';

const cn = withStyles(styles);

export type AdditionalServicesHandle = {
  touchAllFields: () => void;
};

export type AdditionalServicesProps = {
  ref?: RefObject<AdditionalServicesHandle | null>;
  data: vp.PlanSettings | undefined;
  currency: string;
  planType: 'pay as you go' | 'subscription';
  useProgressivePricing: boolean;
  onValidChange: (isValid: boolean) => void;
  onValuesChange: (values: Record<string, string | boolean>) => void;
};

const AdditionalServices: FC<AdditionalServicesProps> = ({ ref, data, currency, planType, useProgressivePricing, onValidChange, onValuesChange }) => {
    const services = useMemo(
      () => (data ? Object.entries(data).filter(([key]) => !PLAN_MODEL_KEYS.has(key)) : []),
      [data],
    );

    const cardRefs = useRef<Map<string, PlanSettingCardHandle>>(new Map());
    const validityMapRef = useRef<Record<string, boolean>>({});
    const valuesMapRef = useRef<Record<string, Record<string, string | boolean>>>({});

    const handleServiceValidChange = useCallback(
      (serviceName: string, isValid: boolean) => {
        validityMapRef.current = { ...validityMapRef.current, [serviceName]: isValid };
        onValidChange(services.every(([name]) => validityMapRef.current[name] === true));
      },
      [services, onValidChange],
    );

    const handleServiceValuesChange = useCallback((serviceName: string, values: Record<string, string | boolean>) => {
      valuesMapRef.current = { ...valuesMapRef.current, [serviceName]: values };
      const merged = Object.values(valuesMapRef.current).reduce((acc, v) => ({ ...acc, ...v }), {});
      onValuesChange(merged);
    }, [onValuesChange]);

    useEffect(() => {
      if (services.length === 0) onValidChange(true);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useImperativeHandle(
      ref,
      () => ({
        touchAllFields: () => {
          for (const handle of cardRefs.current.values()) handle.touchAllFields();
        },
      }),
      [],
    );

    return (
      <div>
        <div className={cn('container')}>
          {services.map(([name, planSetting], idx) => (
            <div
              key={name}
              onFocus={(e) => {
                if (e.currentTarget.contains(e.relatedTarget as Node)) return;
                services.slice(0, idx).forEach(([n]) => cardRefs.current.get(n)?.touchAllFields());
              }}
            >
              <PlanSettingCard
                ref={(el) => {
                  if (el) cardRefs.current.set(name, el);
                  else cardRefs.current.delete(name);
                }}
                serviceName={name}
                planSetting={planSetting}
                currency={currency}
                planType={planType}
                useProgressivePricing={useProgressivePricing}
                onValidChange={handleServiceValidChange}
                onValuesChange={handleServiceValuesChange}
              />
            </div>
          ))}
        </div>
      </div>
    );
};

export default AdditionalServices;
